import { lazy } from 'react'

import {
  ANALYTICS_PATH,
  BOOKING_ARRIVAL_PATH,
  BOOKING_DETAILS_PATH,
  BOOKING_FLEET_PATH,
  CATALOG_PATH,
  DOCUMENT_TOKEN_PATH,
  DRIVER_FLEET_PATH,
  DRIVER_PATH,
  HOME_PATH,
  ICON_SHOWCASE_PATH,
  LOGIN_PATH,
  PASSWORD_RECOVERY_PATH,
  PROFILE_PATH,
  RENTAL_INFO_PATH,
  RENTALS_PATH,
  REQUEST_PHOTOS_FLEET_PATH,
  SERVICE_INFO_PATH,
  SERVICES_PATH,
  SETTINGS_PATH,
  ACCOUNT_SETTINGS_PATH,
  SURVEY_PATH,
  VEHICLE_EVENTS_INFO_PATH,
  VEHICLE_EVENTS_PATH,
  VW_DOCUMENT_TOKEN_PATH,
  VW_PUBLISH_CLOSE_PATH,
  WORKSHOP_EVENTS_INFO_PATH,
  WORKSHOP_EVENTS_PATH,
  MECHANIC_EVENTS_PATH,
  MECHANIC_EVENTS_INFO_PATH,
} from './routePaths'

export * from './routePaths'

// Layouts
const MainLayout = lazy(() => import('../layouts/MainLayout'))
const LoginLayout = lazy(() => import('../layouts/LoginLayout'))
const DriverLayout = lazy(() => import('../layouts/DriverLayout'))
const SurveyLayout = lazy(() => import('../layouts/SurveyLayout'))
const RequestPhotosLayout = lazy(() => import('../layouts/RequestPhotosLayout'))
const BookingLayout = lazy(() => import('../layouts/BookingLayout'))
const VehicleEventsLayout = lazy(() => import('../layouts/VehicleEventsLayout'))

// Pages
const Login = lazy(() => import('../pages/Login'))
const Catalog = lazy(() => import('../pages/Catalog'))
const Services = lazy(() => import('../pages/Services'))
const Rentals = lazy(() => import('../pages/Rentals/Rentals'))
const Home = lazy(() => import('../pages/Home'))
const Analytics = lazy(() => import('../pages/Analytics'))
const ServiceInfo = lazy(() => import('../pages/ServiceInfo'))
const Driver = lazy(() => import('../pages/Driver'))
const Survey = lazy(() => import('../pages/Survey'))
const RequestPhotos = lazy(() => import('../pages/RequestPhotos'))
const Settings = lazy(() => import('../pages/Settings'))
const Profile = lazy(() => import('../pages/Profile'))
const Booking = lazy(() => import('../pages/Booking/Booking'))
const BookingArrival = lazy(() => import('../pages/Booking/BookingArrival'))
const BookingDetails = lazy(() => import('../pages/Booking/BookingDetails'))
const VehicleEvents = lazy(() => import('../pages/VehicleEvents/VehicleEvents'))
const WorkshopEvents = lazy(() =>
  import('../pages/VehicleEvents/WorkshopEvents'),
)
const MechanicEvents = lazy(() =>
  import('../pages/VehicleEvents/MechanicEvents'),
)
const MechanicEventsInfo = lazy(() =>
  import('../pages/VehicleEvents/MechanicEventsInfo'),
)
const VehicleEventsInfo = lazy(() =>
  import('../pages/VehicleEvents/VehicleEventsInfo'),
)
const WorkshopEventsInfo = lazy(() =>
  import('../pages/VehicleEvents/WorkshopEventsInfo'),
)
const DocumentToken = lazy(() => import('../pages/DocumentToken/DocumentToken'))
const IconShowcase = lazy(() => import('../pages/IconShowcase'))
const VwDocumentTest = lazy(() =>
  import('../pages/VwPublishClose/VwDocumentTest'),
)
const VwPublishClose = lazy(() =>
  import('../pages/VwPublishClose/VwPublishClose'),
)
const AccountSettings = lazy(() =>
  import('../pages/AccountSettings/AccountSettingsPage'),
)

const routes = {
  Login: {
    path: LOGIN_PATH,
    Layout: LoginLayout,
    Component: Login,
  },
  PasswordRecovery: {
    path: PASSWORD_RECOVERY_PATH,
  },
  Catalog: {
    path: CATALOG_PATH,
    Layout: MainLayout,
    Component: Catalog,
    isPrivate: true,
    isDocTokenPage: true,
    layoutProps: {
      hasCustomHeader: true,
    },
  },
  Services: {
    path: SERVICES_PATH,
    Layout: MainLayout,
    Component: Services,
    isPrivate: true,
    layoutProps: {
      hasCustomHeader: true,
    },
  },
  Rentals: {
    path: RENTALS_PATH,
    Layout: MainLayout,
    Component: Rentals,
    isPrivate: true,
    layoutProps: {
      hasCustomHeader: true,
    },
  },
  ServiceInfo: {
    path: SERVICE_INFO_PATH,
    Layout: MainLayout,
    Component: ServiceInfo,
    isPrivate: true,
    isDocTokenPage: true,
    layoutProps: {
      hasCustomHeader: true,
      shouldHideNavBarOnMobile: true,
    },
  },
  RentalInfo: {
    path: RENTAL_INFO_PATH,
    Layout: MainLayout,
    Component: ServiceInfo,
    isPrivate: true,
    isDocTokenPage: true,
    layoutProps: {
      hasCustomHeader: true,
      shouldHideNavBarOnMobile: true,
    },
  },
  DriverFleet: {
    path: DRIVER_FLEET_PATH,
    Layout: DriverLayout,
    Component: Driver,
  },
  Driver: {
    path: DRIVER_PATH,
    Layout: DriverLayout,
    Component: Driver,
  },
  Analytics: {
    path: ANALYTICS_PATH,
    Layout: MainLayout,
    Component: Analytics,
  },
  Home: {
    path: HOME_PATH,
    Layout: MainLayout,
    Component: Home,
  },
  Settings: {
    path: SETTINGS_PATH,
    Layout: MainLayout,
    Component: Settings,
    isPrivate: true,
    layoutProps: {
      hasCustomHeader: true,
      ignoreNavDesktop: true,
    },
  },
  AccountSettings: {
    path: ACCOUNT_SETTINGS_PATH,
    Layout: MainLayout,
    Component: AccountSettings,
    isPrivate: true,
    layoutProps: {},
  },
  Profile: {
    path: PROFILE_PATH,
    Layout: MainLayout,
    Component: Profile,
    isPrivate: true,
    layoutProps: {
      hasCustomHeader: true,
    },
  },
  Survey: {
    path: SURVEY_PATH,
    Layout: SurveyLayout,
    Component: Survey,
  },
  BookingFleet: {
    path: BOOKING_FLEET_PATH,
    Layout: BookingLayout,
    Component: Booking,
  },
  BookingArrival: {
    path: BOOKING_ARRIVAL_PATH,
    Layout: BookingLayout,
    Component: BookingArrival,
  },
  BookingDetails: {
    path: BOOKING_DETAILS_PATH,
    Layout: BookingLayout,
    Component: BookingDetails,
  },
  VehicleEvents: {
    path: VEHICLE_EVENTS_PATH,
    Layout: VehicleEventsLayout,
    Component: VehicleEvents,
  },
  WorkshopEvents: {
    path: WORKSHOP_EVENTS_PATH,
    Layout: VehicleEventsLayout,
    Component: WorkshopEvents,
    isPrivate: true,
  },
  MechanicEvents: {
    path: MECHANIC_EVENTS_PATH,
    Layout: VehicleEventsLayout,
    Component: MechanicEvents,
    isPrivate: true,
  },
  VehicleEventsInfo: {
    path: VEHICLE_EVENTS_INFO_PATH,
    Layout: VehicleEventsLayout,
    Component: VehicleEventsInfo,
    isPrivate: true,
  },
  WorkshopEventsInfo: {
    path: WORKSHOP_EVENTS_INFO_PATH,
    Layout: VehicleEventsLayout,
    Component: WorkshopEventsInfo,
    isPrivate: true,
  },
  MechanicEventsInfo: {
    path: MECHANIC_EVENTS_INFO_PATH,
    Layout: VehicleEventsLayout,
    Component: MechanicEventsInfo,
    isPrivate: true,
  },
  DocumentToken: {
    path: DOCUMENT_TOKEN_PATH,
    Layout: LoginLayout,
    Component: DocumentToken,
    isDocTokenPage: true,
  },
  RequestPhotos: {
    path: REQUEST_PHOTOS_FLEET_PATH,
    Layout: RequestPhotosLayout,
    Component: RequestPhotos,
  },
  VwDocumentTest: {
    path: VW_DOCUMENT_TOKEN_PATH,
    Layout: LoginLayout,
    Component: VwDocumentTest,
  },
  VwPublishClose: {
    path: VW_PUBLISH_CLOSE_PATH,
    Layout: LoginLayout,
    Component: VwPublishClose,
    isDocTokenPage: true,
  },
  ...(process.env.NODE_ENV === 'development' && {
    IconShowcase: {
      path: ICON_SHOWCASE_PATH,
      Layout: MainLayout,
      Component: IconShowcase,
      isPrivate: true,
      layoutProps: {
        hasCustomHeader: true,
      },
    },
  }),
}

export default routes
